import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowLeft, ShoppingBag, History, List, Trash2, Edit } from 'lucide-react';
import ShoppingItemCard from './ShoppingItemCard';
import ShoppingHistory from './ShoppingHistory';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useShopping } from '../../hooks/useShopping';
import { parseShoppingItem } from '../../utils/smartItemParser';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { getActiveShoppingItems, getCompletedShoppingItems } from '../../utils/shoppingHelpers';
import { formatDistanceToNow } from 'date-fns';

export default function ShoppingDashboard() {
  const { family } = useFamilyContext();
  const {
    items,
    loading,
    error,
    addItem,
    updateItem,
    deleteItem,
    purchaseItem,
    refreshData
  } = useShopping();
  
  // Tab and view state
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'history'
  const [inputValue, setInputValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const inputRef = useRef(null);

  // Enhanced common items with categories and smart suggestions
  const smartSuggestions = {
    frequent: ['Milk', 'Bread', 'Eggs', 'Cheese', 'Bananas', 'Apples'],
    recent: ['Chocolate Milk', 'Pizza', 'Ice Cream', 'Crackers'],
    seasonal: ['Pumpkins', 'Hot Chocolate', 'Soup', 'Oranges']
  };

  const commonItemsByCategory = {
    dairy: ['Milk', 'Cheese', 'Yogurt', 'Butter', 'Cream', 'Ice Cream'],
    produce: ['Apples', 'Bananas', 'Carrots', 'Lettuce', 'Tomatoes', 'Onions'],
    snacks: ['Crackers', 'Chips', 'Cookies', 'Granola Bars', 'Chocolate'],
    pantry: ['Bread', 'Rice', 'Pasta', 'Canned Goods', 'Spices'],
    other: []
  };

  // Auto-focus input when it becomes available
  useEffect(() => {
    if (!loading && inputRef.current && !isAdding) {
      try {
        inputRef.current.focus();
      } catch (error) {
        console.error('Focus failed:', error);
      }
    }
  }, [loading, isAdding]);

  // Handle visibility change to refocus when returning to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !loading && inputRef.current && !isAdding) {
        try {
          inputRef.current.focus();
        } catch (error) {
          console.error('Visibility focus failed:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loading, isAdding]);



  // Handle adding new items
  const handleAddItem = async (itemName) => {
    if (!itemName.trim()) return;
    
    setIsAdding(true);
    try {
      const parsedItem = parseShoppingItem(itemName);
      await addItem(parsedItem);
      setInputValue('');
      // Refocus input after adding
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleInputSubmit = (e) => {
    e.preventDefault();
    handleAddItem(inputValue);
  };

  // Filter items
  const activeItems = getActiveShoppingItems(items);
  const completedItemsList = getCompletedShoppingItems(items);

  // Group items by category for enhanced display
  const categorizeItems = (items) => {
    const categories = {
      dairy: [],
      produce: [],
      snacks: [],
      pantry: [],
      other: []
    };

    items.forEach(item => {
      const itemName = item.name.toLowerCase();
      let categorized = false;
      
      for (const [category, keywords] of Object.entries(commonItemsByCategory)) {
        if (category === 'other') continue;
        if (keywords.some(keyword => itemName.includes(keyword.toLowerCase()))) {
          categories[category].push(item);
          categorized = true;
          break;
        }
      }
      
      if (!categorized) {
        categories.other.push(item);
      }
    });

    return categories;
  };

  const categorizedItems = categorizeItems(activeItems);

  // Handler functions
  const handleQuickAdd = async (itemData) => {
    try {
      await addItem(typeof itemData === 'string' ? { name: itemData } : itemData);
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleItemToggle = async (itemId) => {
    const item = items.find(i => i.id === itemId);
    if (item.status === 'purchased') {
      await updateItem(itemId, { status: 'pending' });
    } else {
      await purchaseItem(itemId);
    }
  };

  const handleItemDelete = async (itemId) => {
    try {
      await deleteItem(itemId);
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  // Get family member by ID
  const getFamilyMember = (memberId) => {
    const allMembers = [...(family?.parents || []), ...(family?.kids || [])];
    return allMembers.find(m => m.id === memberId);
  };

  // Compact item component
  const CompactItem = ({ item }) => {
    const requester = getFamilyMember(item.addedBy || item.requested_by);
    const isCompleted = item.status === 'purchased';
    
    return (
      <div className={`flex items-center p-3 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors ${isCompleted ? 'opacity-60' : ''} group relative`}>
        {/* Checkbox */}
        <button
          onClick={() => handleItemToggle(item.id)}
          className={`w-5 h-5 border-2 rounded flex items-center justify-center mr-3 transition-colors flex-shrink-0 ${
            isCompleted 
              ? 'bg-coral-500 border-coral-500 text-white' 
              : 'border-stone-300 hover:border-coral-500'
          }`}
        >
          {isCompleted && <CheckCircle className="w-3 h-3" />}
        </button>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className={`font-medium text-slate-900 ${isCompleted ? 'line-through' : ''}`}>
            {item.name}
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-stone-500">
            {/* Quantity (only if not 1) */}
            {item.quantity && item.quantity !== 1 && (
              <>
                <span>Qty: {item.quantity}</span>
                <div className="w-1 h-1 bg-stone-400 rounded-full"></div>
              </>
            )}
            
            {/* Time ago */}
            <span>{formatDistanceToNow(new Date(item.created_at || item.createdAt), { addSuffix: true })}</span>
            
            {/* Requester */}
            {requester && (
              <>
                <div className="w-1 h-1 bg-stone-400 rounded-full"></div>
                <div className="flex items-center gap-1">
                  <AvatarOrInitial
                    avatar={requester.avatar}
                    name={requester.name}
                    size="xs"
                    className="w-4 h-4"
                  />
                  <span>{requester.name}</span>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Direct Action Icons */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => {
              // TODO: Implement edit functionality
              console.log('Edit item:', item.id);
            }}
            className="text-stone-400 hover:text-azure-600 p-1 rounded hover:bg-azure-50 transition-colors"
            title="Edit item"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleItemDelete(item.id)}
            className="text-stone-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
            title="Delete item"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Category renderer
  const renderCategory = (categoryName, items) => {
    if (items.length === 0) return null;

    const categoryIcons = {
      dairy: '🥛',
      produce: '🍎',
      snacks: '🍪',
      pantry: '🍞',
      other: '📦'
    };

    return (
      <div key={categoryName} className="border-b border-stone-100 last:border-b-0">
        {/* Category Header */}
        <div className="flex items-center justify-between p-3 bg-stone-50 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-white rounded flex items-center justify-center border border-stone-200 text-xs">
              {categoryIcons[categoryName] || '📦'}
            </div>
            <span className="capitalize font-medium text-stone-700 text-sm">{categoryName}</span>
          </div>
          <span className="text-xs font-medium text-stone-500 bg-white px-2 py-1 rounded-full border border-stone-200">
            {items.length}
          </span>
        </div>
        
        {/* Items */}
        <div className="group">
          {items.map((item) => (
            <CompactItem key={item.id} item={item} />
          ))}
        </div>
      </div>
    );
  };

  // Header component
  const renderHeader = () => (
    <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
      <div className="px-4 py-3">
        {/* Top row: Back button, Section name with color bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              style={{ minWidth: '44px', minHeight: '44px' }}
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            
            <div className="flex items-center gap-3">
              {/* Color bar */}
              <div className="w-2 h-6 bg-coral-500 rounded-full"></div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">Shopping</h1>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom row: Tab Navigation */}
      <div className="px-0">
        <div className="flex">
          <button 
            onClick={() => setActiveTab('list')}
            className={`flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50 ${
              activeTab === 'list'
                ? 'border-coral-500 text-slate-900 bg-coral-50'
                : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
            }`}
            style={{ minHeight: '48px' }}
          >
            <div className="flex items-center justify-center gap-2">
              <List className="w-4 h-4" />
              <span>Shopping List</span>
            </div>
          </button>
          <button 
            onClick={() => setActiveTab('history')}
            className={`flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50 ${
              activeTab === 'history'
                ? 'border-coral-500 text-slate-900 bg-coral-50'
                : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
            }`}
            style={{ minHeight: '48px' }}
          >
            <div className="flex items-center justify-center gap-2">
              <History className="w-4 h-4" />
              <span>History</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-600 mx-auto mb-4"></div>
          <p className="text-stone-600">Loading shopping list...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <h2 className="text-lg font-bold mb-2">Something went wrong</h2>
          <p className="text-stone-600 mb-6">{error}</p>
          <button 
            onClick={refreshData}
            className="bg-coral-500 text-white px-4 py-2 rounded-lg hover:bg-coral-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {renderHeader()}
      
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">
        {activeTab === 'list' && (
          <div>
            {/* Streamlined Add Section */}
            <div className="bg-coral-50 p-4 m-4 rounded-2xl border border-coral-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Add to list...</h3>
              <p className="text-stone-600 mb-4 text-sm">Quickly add items or choose from suggestions</p>
              
              <form onSubmit={handleInputSubmit} className="mb-3">
                <div className="flex gap-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="What do you need?"
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-coral-200 focus:border-coral-400"
                    disabled={isAdding}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isAdding}
                    className="bg-slate-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-stone-800 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors min-w-[60px]"
                  >
                    {isAdding ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto"></div>
                    ) : (
                      '+'
                    )}
                  </button>
                </div>
              </form>
              
              {/* Quick suggestion buttons */}
              <div className="flex gap-2 flex-wrap">
                {smartSuggestions.frequent.slice(0, 6).map((item) => (
                  <button
                    key={`frequent-${item}`}
                    onClick={() => handleQuickAdd(item)}
                    className="bg-white border border-stone-300 px-3 py-1.5 rounded-full text-sm font-medium text-stone-700 hover:bg-stone-100 hover:border-stone-400 transition-all"
                    disabled={isAdding}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

                         {/* Shopping Items List - Categorized */}
             {activeItems.length > 0 && (
               <div className="bg-white mx-4 rounded-2xl border border-stone-200 overflow-hidden">
                 {renderCategory('dairy', categorizedItems.dairy)}
                 {renderCategory('produce', categorizedItems.produce)}
                 {renderCategory('snacks', categorizedItems.snacks)}
                 {renderCategory('pantry', categorizedItems.pantry)}
                 {renderCategory('other', categorizedItems.other)}
               </div>
             )}

            {/* Completed Items (Collapsible) */}
            {completedItemsList.length > 0 && (
              <div className="bg-white mx-4 mt-4 rounded-2xl border border-stone-200 overflow-hidden">
                <button
                  onClick={() => setShowCompleted(!showCompleted)}
                  className="w-full p-4 flex items-center justify-between text-stone-600 hover:bg-stone-50 transition-colors"
                >
                  <span className="font-medium">Completed ({completedItemsList.length})</span>
                  <span className={`transform transition-transform ${showCompleted ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {showCompleted && (
                  <div className="border-t border-stone-200">
                    {completedItemsList.map((item) => (
                      <CompactItem key={item.id} item={item} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {items.length === 0 && (
              <div className="text-center py-16 px-4">
                <ShoppingBag className="w-12 h-12 text-stone-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">Ready to shop?</h3>
                <p className="text-stone-500">Your family shopping list is waiting for the first item!</p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'history' && (
          <div className="p-4">
            <ShoppingHistory />
          </div>
        )}
      </div>
    </div>
  );
}